import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Search } from "lucide-react";

import { sortedCountries, findCountry } from "../../i18n/countries";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

/**
 * CountrySelect — searchable full-name country dropdown.
 * Stores ISO-2, displays localized full name.
 *
 * Props:
 *   value:      string ISO2 ("DE", "AT", ...)
 *   onChange:   (iso2) => void
 *   disabled:   bool
 *   error:      bool
 *   testIdRoot: string
 */
export function CountrySelect({
  value,
  onChange,
  disabled,
  error,
  testIdRoot = "country",
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith("en") ? "en" : "de";

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = findCountry(value);

  const list = useMemo(() => {
    const all = sortedCountries(lng);
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => {
      const name = (lng === "en" ? c.name_en : c.name_de).toLowerCase();
      return name.includes(q) || c.iso2.toLowerCase().includes(q);
    });
  }, [lng, query]);

  const pick = (iso2) => {
    onChange(iso2);
    setOpen(false);
    setQuery("");
  };

  const errRing = error
    ? "!ring-1 !ring-status-unavailable !border-status-unavailable"
    : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`${testIdRoot}-trigger`}
          className={`inline-flex h-10 w-full items-center gap-2 rounded-md border border-border-subtle bg-surface px-3 text-left text-sm text-text-primary transition-colors hover:bg-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne disabled:opacity-60 ${errRing}`}
        >
          <span aria-hidden className="text-base leading-none">
            {selected.flag}
          </span>
          <span className="flex-1 truncate">
            {lng === "en" ? selected.name_en : selected.name_de}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-text-secondary" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        data-testid={`${testIdRoot}-content`}
        className="w-[--radix-popover-trigger-width] min-w-[280px] border-border-subtle bg-surface p-0"
      >
        <div className="flex items-center gap-2 border-b border-border-subtle px-3 py-2">
          <Search className="h-4 w-4 text-text-secondary" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("forms.country_search_placeholder")}
            autoFocus
            data-testid={`${testIdRoot}-search`}
            className="w-full bg-transparent text-sm outline-none placeholder:text-text-secondary"
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {list.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-text-secondary">
              {t("forms.country_no_results")}
            </div>
          ) : (
            list.map((c) => {
              const active = c.iso2 === selected.iso2;
              return (
                <button
                  key={c.iso2}
                  type="button"
                  onClick={() => pick(c.iso2)}
                  data-testid={`${testIdRoot}-option-${c.iso2}`}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-offwhite ${
                    active ? "bg-offwhite" : ""
                  }`}
                >
                  <span aria-hidden className="text-base leading-none">
                    {c.flag}
                  </span>
                  <span className="flex-1 truncate">
                    {lng === "en" ? c.name_en : c.name_de}
                  </span>
                  {active && <Check className="h-4 w-4 text-champagne" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
